// ---------------------------------------------------------------------------
// The sub-selections that a Product switch keeps: the stage (or the content
// type), the set and the layout.
//
// A user who works on a run of products keeps one stage, one set and one
// layout for all of them. Thus a product switch keeps each sub-selection that
// the new product offers. Only a sub-selection that the new product does not
// offer goes to the default of that product.
//
// That default is temporary. The switch remembers the value that the user
// wants, and the next product that offers the value gets it back: CLion on
// Beta → DataGrip (no Beta, so it opens on Release) → GoLand opens on Beta.
// When the user changes the selector after a switch, the new value is the
// choice, and the remembered value does not apply.
// ---------------------------------------------------------------------------

/** A value that the user wants, and the value that the page shows for it. */
export interface HeldChoice<Value> {
  wanted: Value;
  shown: Value;
}

export interface KeepChoiceInput<Value> {
  /** The value that the page shows now. Undefined when the page has no such selector. */
  held: Value | undefined;
  /** The choice that the last product switch remembered. */
  remembered: HeldChoice<Value> | undefined;
  /**
   * False when the held value is a default and not a choice of the user, for
   * example the only stage of a product. True when not given.
   */
  isHeldChosen?: boolean;
  /** The values that the new product offers. */
  offered: readonly Value[];
  /** The value that the new product opens on. */
  fallback: Value;
}

export interface KeptChoice<Value> {
  /** The value that the new product shows. */
  value: Value;
  /** The choice to remember for the next switch. Undefined when there is no choice. */
  remembered: HeldChoice<Value> | undefined;
}

/** The value of one sub-selector after a product switch. */
export function keepChoice<Value>({
  held,
  remembered,
  isHeldChosen = true,
  offered,
  fallback,
}: KeepChoiceInput<Value>): KeptChoice<Value> {
  // The remembered value applies only while the page shows the value that the
  // last switch gave. Any other held value is a later choice of the user.
  const wanted =
    remembered && remembered.shown === held ? remembered.wanted : isHeldChosen ? held : undefined;
  if (wanted === undefined) return {value: fallback, remembered: undefined};
  const value = offered.includes(wanted) ? wanted : fallback;
  return {value, remembered: {wanted, shown: value}};
}
